import { formatCents, formatCount, formatFixed, tt } from '@agos/shared'

/**
 * The loyalty programme as the customer sees it, and as the server will actually honour it.
 *
 * `GET /loyalty/public-info` answers the rates as **strings** — `pointsPerEuro: "1.0000"`,
 * `pointValue: "0.0100"` — so every one of them is parsed rather than used. §14.7.
 *
 * **The level is the order count, not the points.** Measured on the restored data: `bronze` spans 0–44
 * orders, the one `silver` has 53, and the three `gold_master` have 166–184, against thresholds of
 * 50/100/150/200. A bronze customer holding 937 points is not an inconsistency, and a screen that
 * implied the balance drives the tier would be telling them something untrue.
 *
 * **Points below the minimum are worth nothing, and it is worth saying so.** `pointsWorth` refuses the
 * whole redemption when `balance < minRedeem`, and 846 of the 2 465 customers hold points and cannot
 * use them. Showing a balance without the threshold is how a customer discovers that at the checkout.
 */

export interface LoyaltyRates {
  isActive: boolean
  programName: string
  pointsPerEuro: number
  /** Euros per point. `0.0100` on this data — one cent. */
  pointValue: number
  minRedeemPoints: number
  thresholds: { key: string; orders: number }[]
  /**
   * Whether points may be spent on an order that already has a zľava - `BACKEND.md` §10A.217.
   *
   * On `PUBLIC_FIELDS` precisely so this screen can answer the question **before** the basket is
   * priced: switched off, a customer who dragged the slider beside a coupon would watch their points
   * buy nothing, and the server would have been right to refuse them.
   */
  combinePointsWithDiscounts: boolean
}

/** Numbers arrive as strings from this endpoint; `Number('')` is 0 and `Number(null)` is 0 too. */
function numberOf(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function ratesOf(payload: unknown): LoyaltyRates | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  return {
    isActive: row.isActive === true,
    programName: typeof row.programName === 'string' ? row.programName : '',
    pointsPerEuro: numberOf(row.pointsPerEuro, 1),
    // The server's own fallbacks, from `pointsWorth`: 0,01 € a point and a hundred-point minimum.
    pointValue: numberOf(row.pointValue, 0.01),
    minRedeemPoints: Math.trunc(numberOf(row.minRedeemPoints, 100)),
    thresholds: [
      { key: 'silver', orders: Math.trunc(numberOf(row.silverThreshold, 0)) },
      { key: 'gold', orders: Math.trunc(numberOf(row.goldThreshold, 0)) },
      { key: 'gold_master', orders: Math.trunc(numberOf(row.goldMasterThreshold, 0)) },
      { key: 'elite_master', orders: Math.trunc(numberOf(row.eliteMasterThreshold, 0)) },
    ].filter((level) => level.orders > 0),
    /* §10A.217 - **`!== false`, not `=== true`.** Every other flag here reads `=== true` because
     * *absent* means *not switched on*; this one ships **on**, so an older server that does not
     * answer the field must read as *combining allowed* rather than as a refusal the server is not
     * making. The same reasoning as `programFormOf` on the manager side. */
    combinePointsWithDiscounts: row.combinePointsWithDiscounts !== false,
  }
}

/**
 * What a number of points is worth, in cents.
 *
 * `Money.cents(pointValue × requested)` — **multiply first, round once**. Rounding the point to a cent
 * and then multiplying gives a different answer for any rate that is not a whole number of cents: at
 * 0,015 € a point, 10 points are worth 15 cents the server's way and 20 cents the other way. The
 * configured rate happens to be exactly one cent, which is precisely why the order matters — nothing
 * here would ever show the mistake.
 */
export function pointsValueCents(rates: LoyaltyRates | null, points: number): number {
  if (rates === null || points <= 0) return 0
  return Math.round(rates.pointValue * points * 100)
}

/**
 * The most points this customer may actually spend.
 *
 * Zero below the minimum: `pointsWorth` refuses the whole redemption rather than trimming it, and a
 * slider that offers points the server will decline is a slider that lies.
 *
 * **§9.278 — and the order is the second ceiling.** This returned the balance alone, so a customer
 * with 3 000 points on a 12 € basket was offered 3 000 and shown *−30,00 €*. Legacy takes the lower
 * of two (`Checkout.tsx:1674`): the balance, and what the cart can absorb at the point value.
 *
 * **`absorbableCents` must exclude the points discount itself.** Legacy's comment is the warning:
 * *"Cart subtotal excluding points-derived discount so the cap doesn't shrink as the user moves the
 * slider."* A ceiling computed from a total that already has the points taken off chases itself
 * downwards on every drag.
 *
 * Omitting it keeps the old behaviour, so callers that genuinely mean *"whatever they hold"* — the
 * profile's balance card — are unchanged.
 */
export function maxRedeemable(
  rates: LoyaltyRates | null,
  balance: number,
  absorbableCents?: number,
): number {
  if (rates === null || !rates.isActive) return 0
  if (balance < rates.minRedeemPoints) return 0
  if (absorbableCents === undefined) return balance
  const perPoint = Math.round(rates.pointValue * 100)
  // A point worth nothing cannot be capped by the cart — dividing would be by zero, and the balance
  // is then the only meaningful ceiling.
  if (perPoint <= 0) return balance
  // Floor, so the cap never proposes a point the cart cannot pay for.
  const byCart = Math.floor(Math.max(0, absorbableCents) / perPoint)
  return Math.min(balance, byCart)
}

/**
 * The quick amounts legacy offers under the box — §9.278, owner O23.
 *
 * *"Missing things like Min, 200, 500, 1000, Max, Polovica… like in old system."* Legacy's own list is
 * `Min`, 100, 500, 1000, `Polovica`, `Max` (`Checkout.tsx:1762`), and the round numbers are dropped
 * when they fall outside what can actually be spent — an amount that cannot be chosen is a button that
 * does nothing.
 */
export interface QuickAmount {
  label: string
  points: number
}

export function quickAmounts(rates: LoyaltyRates | null, ceiling: number): QuickAmount[] {
  if (rates === null || ceiling <= 0) return []
  const min = rates.minRedeemPoints
  if (ceiling < min) return []
  const half = Math.floor(ceiling / 2)
  const amounts: QuickAmount[] = [{ label: `${tt('Min')} (${formatCount(min)})`, points: min }]
  for (const round of [100, 500, 1000]) {
    if (round > min && round < ceiling) amounts.push({ label: formatCount(round), points: round })
  }
  // Only when it is a distinct choice: at a ceiling of 200 with a minimum of 100, *Polovica* is *Min*.
  if (half > min && half < ceiling) amounts.push({ label: tt('Polovica'), points: half })
  amounts.push({ label: `${tt('Max')} (${formatCount(ceiling)})`, points: ceiling })
  return amounts
}

/**
 * What the tier multiplies this order's earnings by — §9.278, owner O23.
 *
 * *"Show násobič bodov for your current level (for example Majster chuti: Za túto objednávku získate
 * body s násobičom ×1.20)."* Said only above ×1, which is the rule `happyHoursPointsNote` already
 * follows one card away: the ordinary rate is not a fact worth a line.
 */
export function multiplierNote(levelName: string | null, multiplier: number): string {
  if (!Number.isFinite(multiplier) || multiplier <= 1) return ''
  // `formatFixed`, not `formatDecimal`: the latter trims a trailing zero and ×1,2 reads as a typo.
  const rate = `×${formatFixed(multiplier, 2)}`
  const prefix = levelName === null || levelName.trim() === '' ? '' : `${levelName.trim()}: `
  return `${prefix}${tt('Za túto objednávku získate body s násobičom')} ${rate}`
}

/** Why the points cannot be spent, or `''` when they can. */
/**
 * Whether this basket's points are refused because something else is already discounting it.
 *
 * §10A.217. **`otherDiscountCents` is every other source summed**, which is the same question the
 * server asks of its own running total: naming the coupon alone would refuse points beside a coupon
 * and allow them beside a referral or happy hours.
 *
 * <p>A pure function rather than an expression in the checkout, because it decides a price the
 * server also decides - and a rule that lives on one side of that seam is the shape of three owner
 * bugs in one session (`agos-a-test-on-one-side-of-a-seam`).
 */
export function pointsAreBlocked(
  rates: LoyaltyRates | null,
  otherDiscountCents: number,
): boolean {
  if (rates === null) return false
  return !rates.combinePointsWithDiscounts && otherDiscountCents > 0
}

export function redemptionNote(
  rates: LoyaltyRates | null,
  balance: number,
  /**
   * Whether this order already carries a discount from another source - §10A.217.
   *
   * Optional and defaulting to *no*, so every existing caller keeps its behaviour. Answered by the
   * screen rather than derived here, because *what else is on this basket* is not a fact about the
   * loyalty programme.
   */
  hasAnotherDiscount = false,
): string {
  if (rates === null || !rates.isActive) return tt('Vernostný program nie je zapnutý.')
  /* §10A.217 - **before the balance checks, because it is true whatever the balance is.** Said here
   * rather than by hiding the slider silently: a customer with 1 596 points who sees the control
   * vanish when a coupon is applied has been given no reason, and the reason is the one thing that
   * lets them choose the other way round. */
  if (hasAnotherDiscount && !rates.combinePointsWithDiscounts) {
    return tt('Body sa nedajú kombinovať so zľavou ani s kupónom na tej istej objednávke.')
  }
  if (balance <= 0) return tt('Zatiaľ nemáte žiadne body.')
  if (balance < rates.minRedeemPoints) {
    const missing = rates.minRedeemPoints - balance
    return `${tt('Body sa dajú uplatniť od')} ${formatCount(rates.minRedeemPoints)} - ${tt('chýba vám')} ${formatCount(missing)}.`
  }
  return ''
}

/** `120 bodov · 1,20 €` — the balance and what it is worth, which are different facts. */
export function balanceNote(rates: LoyaltyRates | null, balance: number): string {
  const worth = pointsValueCents(rates, balance)
  return `${formatCount(balance)} ${tt('bodov')} · ${formatCents(worth)}`
}

/**
 * The next tier and how many orders away it is.
 *
 * Orders, not points, and not money — the levels are set against `order_count` and the data agrees:
 * bronze tops out at 44 orders, the one silver has 53, gold_master runs 166–184.
 */
export function nextLevel(
  rates: LoyaltyRates | null,
  orderCount: number,
): { key: string; ordersAway: number } | null {
  if (rates === null) return null
  const ahead = rates.thresholds
    .filter((level) => level.orders > orderCount)
    .sort((left, right) => left.orders - right.orders)[0]
  return ahead === undefined ? null : { key: ahead.key, ordersAway: ahead.orders - orderCount }
}

/**
 * The tier this customer holds, named from the franchise's own thresholds — §9.218.
 *
 * **Never a hard-coded five.** Legacy's own comment on this resolution is emphatic — *"must NEVER
 * fall back to hardcoded labels when any custom level exists — the admin owns the taxonomy"* — so
 * the highest threshold at or below the order count wins, and the stored `loyaltyLevel` is the
 * fallback for a franchise that has configured none.
 */
export function currentLevelKey(
  rates: LoyaltyRates | null,
  orderCount: number,
  stored: string | null | undefined,
): string {
  const held = (rates?.thresholds ?? [])
    .filter((level) => level.orders <= orderCount)
    .sort((left, right) => right.orders - left.orders)[0]
  return held?.key ?? (stored ?? '')
}

/**
 * How a tier is written on screen — §9.246, FB-406, the owner's N7.
 *
 * **The name, the emoji and the colour have always been on the wire and the badge printed the key.**
 * `GET /loyalty/public-info` answers `levels: [{key, name, emoji, color, minOrders, multiplier}]` —
 * measured, five of them — and `ratesOf` reads only the four flat `*Threshold` numbers beside it. So
 * a Gold Master customer read `Gold_master` in the theme's orange, which is the owner's *"placeholder,
 * missing naming, and missing emojie"*.
 *
 * **The fallback is legacy's own five**, not the key. Legacy carries the same map for a franchise
 * that has never seeded `loyalty_levels`, and a database key on a customer's screen is never the
 * right answer — least of all one with an underscore in it.
 */
export interface LoyaltyTier {
  key: string
  name: string
  emoji: string
  color: string
  /**
   * What this tier multiplies the order's earned points by — §9.278.
   *
   * The endpoint has always sent it (`multiplier: 1.0` on the starter tier) and this reader dropped
   * it, so the checkout had nothing to say when the owner asked for *"násobič bodov for your current
   * level"*. `1` when absent, which is the ordinary rate and the value `multiplierNote` stays silent
   * about.
   */
  multiplier: number
}

/** Legacy's map, `MojProfil.tsx:1268`, for a franchise with no configured levels. */
const FALLBACK_TIERS: Record<string, Omit<LoyaltyTier, 'key' | 'multiplier'>> = {
  bronze: { name: 'Bronze', emoji: '🥉', color: '#cd7f32' },
  silver: { name: 'Silver', emoji: '🥈', color: '#9ca3af' },
  gold: { name: 'Gold', emoji: '🥇', color: '#eab308' },
  gold_master: { name: 'Gold Master', emoji: '👑', color: '#f59e0b' },
  elite_master: { name: 'Elite Master', emoji: '💎', color: '#8b5cf6' },
}

export function tiersOf(payload: unknown): LoyaltyTier[] {
  const rows = (payload as { levels?: unknown } | undefined)?.levels
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const key = typeof row.key === 'string' ? row.key : ''
    if (key === '') return []
    const fallback = FALLBACK_TIERS[key]
    return [
      {
        key,
        name: typeof row.name === 'string' && row.name.trim() !== '' ? row.name : (fallback?.name ?? key),
        emoji: typeof row.emoji === 'string' ? row.emoji : (fallback?.emoji ?? ''),
        color: typeof row.color === 'string' && row.color.trim() !== '' ? row.color : (fallback?.color ?? ''),
        multiplier: Number.isFinite(Number(row.multiplier)) && Number(row.multiplier) > 0 ? Number(row.multiplier) : 1,
      },
    ]
  })
}

/**
 * The tier to draw for a key, from the franchise's own levels or legacy's map.
 *
 * Answers `null` for a key nothing knows — including the empty one `currentLevelKey` returns for a
 * customer who is not a member — so the caller draws no badge rather than an empty one.
 */
export function tierFor(payload: unknown, key: string): LoyaltyTier | null {
  if (key === '') return null
  const configured = tiersOf(payload).find((tier) => tier.key === key)
  if (configured !== undefined) return configured
  const fallback = FALLBACK_TIERS[key]
  // Legacy's map carries no multiplier, so a tier only this fallback knows earns at the ordinary rate.
  return fallback === undefined ? null : { key, ...fallback, multiplier: 1 }
}

/** What a tier is called, for a sentence rather than a badge. Falls back to the key it was given. */
export function tierName(payload: unknown, key: string): string {
  return tierFor(payload, key)?.name ?? key
}

/** Said under the tier badge, so nobody reads the balance as the thing that promotes them. */
export function levelNote(rates: LoyaltyRates | null, orderCount: number): string {
  const next = nextLevel(rates, orderCount)
  if (next === null) return ''
  return `${tt('Do ďalšej úrovne vám chýba objednávok')}: ${formatCount(next.ordersAway)}`
}

/**
 * How far along this customer is between the tier they hold and the next one — §9.172.
 *
 * Legacy draws a bar here and AGOS drew a sentence. The figures are the same ones `levelNote`
 * already computes; what was missing is the *span*, because a bar needs the threshold behind you as
 * well as the one ahead.
 *
 * Null when there is no next tier — the top of the ladder gets a message, not an empty bar.
 */
export function levelProgress(
  rates: LoyaltyRates | null,
  orderCount: number,
): { from: number; to: number; fraction: number; ordersAway: number; key: string } | null {
  const next = nextLevel(rates, orderCount)
  if (rates === null || next === null) return null
  const behind = rates.thresholds
    .filter((level) => level.orders <= orderCount)
    .sort((left, right) => right.orders - left.orders)[0]
  const from = behind?.orders ?? 0
  const to = from + (next.ordersAway + orderCount - from)
  // A span of nought would divide by zero; two tiers at the same threshold is data nobody should
  // have, but a NaN width is a bar that vanishes rather than an error anybody would notice.
  const span = Math.max(1, to - from)
  return {
    from,
    to,
    fraction: Math.max(0, Math.min(1, (orderCount - from) / span)),
    ordersAway: next.ordersAway,
    key: next.key,
  }
}
